import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClinicProfileDto } from '../../../core/models/clinic-profile.dto';
import { PendingClinicInvitationResponse } from '../../../core/models/pending-clinic-invitation-response.model';
import { NotificationService } from '../../../core/services/notification.service';
import { PermissionService } from '../../../core/services/permission.service';
import { SettingsService } from '../../../core/services/settings.service';
import { UserManagementService } from '../../../core/services/user-management.service';

const isBrowser = typeof localStorage !== 'undefined';

@Component({
  selector: 'app-clinic-profile',
  imports: [CommonModule, FormsModule],
  templateUrl: './clinic-profile.html',
  styleUrl: './clinic-profile.scss',
})
export class ClinicProfile implements OnInit {
  clinicProfile: ClinicProfileDto = {
    id: '',
    name: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    openingTime: '08:00',
    closingTime: '17:00',
  };

  pendingInvitations: PendingClinicInvitationResponse[] = [];
  isLoadingInvitations = false;
  respondingToId: string | null = null;

  isLoading = false;
  isSaving = false;
  isDeactivating = false;
  isClinicDeactivated = false;
  clinicUsers: { role: string; active: boolean }[] = [];

  activeRole = 'DOCTOR';

  constructor(
    private settingsService: SettingsService,
    private notificationService: NotificationService,
    private permissionService: PermissionService,
    private userManagementService: UserManagementService,
  ) {}

  ngOnInit(): void {
    this.loadClinicProfile();
    this.activeRole = isBrowser ? (localStorage.getItem('userRole') ?? 'DOCTOR') : 'DOCTOR';
    if (this.activeRole === 'DOCTOR') {
      this.loadPendingInvitations();
    }
    if (this.isManager) {
      this.loadClinicUsers();
    }
  }

  get canEdit(): boolean {
    return false;
  }

  get isDoctor(): boolean {
    return this.activeRole === 'DOCTOR';
  }

  get isManager(): boolean {
    return this.activeRole === 'MANAGER';
  }

  get canDeactivateClinic(): boolean {
    return (
      !this.isLoading &&
      !this.isDeactivating &&
      !this.isClinicDeactivated &&
      !this.clinicUsers.some(
        (user) => user.active && ['DOCTOR', 'RECEPTION'].includes(user.role.toUpperCase()),
      )
    );
  }

  private loadClinicProfile(): void {
    this.isLoading = true;
    this.settingsService.getClinicProfile().subscribe({
      next: (clinicProfile) => {
        this.clinicProfile = {
          id: clinicProfile.id ?? '',
          name: clinicProfile.name ?? '',
          address: clinicProfile.address ?? '',
          city: clinicProfile.city ?? '',
          province: clinicProfile.province ?? '',
          postalCode: clinicProfile.postalCode ?? '',
          openingTime: clinicProfile.openingTime ?? '08:00',
          closingTime: clinicProfile.closingTime ?? '17:00',
        };
        this.isLoading = false;
      },
      error: (error) => {
        this.notificationService.error(
          'Failed to load practice profile: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
        this.isLoading = false;
      },
    });
  }

  private loadPendingInvitations(): void {
    this.isLoadingInvitations = true;
    this.userManagementService.getPendingClinicInvitations().subscribe({
      next: (invitations) => {
        this.pendingInvitations = invitations;
        this.isLoadingInvitations = false;
      },
      error: () => {
        this.isLoadingInvitations = false;
      },
    });
  }

  private loadClinicUsers(): void {
    this.userManagementService.getClinicUsers().subscribe({
      next: (users) => {
        this.clinicUsers = users;
      },
      error: () => {
        this.clinicUsers = [{ role: 'UNKNOWN', active: true }];
      },
    });
  }

  deactivateClinic(): void {
    if (
      !this.canDeactivateClinic ||
      !window.confirm('Deactivate this clinic? User accounts will remain active.')
    ) {
      return;
    }

    this.isDeactivating = true;
    this.settingsService.deactivateClinic().subscribe({
      next: () => {
        this.isDeactivating = false;
        this.isClinicDeactivated = true;
        this.notificationService.success('Practice deactivated. User accounts were not changed.');
      },
      error: (error) => {
        this.isDeactivating = false;
        this.notificationService.error(
          'Failed to deactivate practice: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
        this.loadClinicUsers();
      },
    });
  }

  respondToInvitation(membershipId: string, accept: boolean): void {
    this.respondingToId = membershipId;
    this.userManagementService.respondToInvitation(membershipId, accept).subscribe({
      next: () => {
        this.respondingToId = null;
        const action = accept ? 'accepted' : 'declined';
        this.notificationService.success(`Practice invitation ${action} successfully.`);
        if (accept) {
          window.location.reload();
        } else {
          this.pendingInvitations = this.pendingInvitations.filter(
            (i) => i.membershipId !== membershipId,
          );
        }
      },
      error: (error) => {
        this.respondingToId = null;
        this.notificationService.error(
          'Failed to respond to invitation: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
      },
    });
  }

  saveClinicProfile(): void {
    if (!this.canEdit) return;
    this.isSaving = true;
    this.settingsService.updateClinicProfile(this.clinicProfile).subscribe({
      next: (updatedClinicProfile) => {
        this.clinicProfile = {
          id: updatedClinicProfile.id ?? this.clinicProfile.id,
          name: updatedClinicProfile.name ?? '',
          address: updatedClinicProfile.address ?? '',
          city: updatedClinicProfile.city ?? '',
          province: updatedClinicProfile.province ?? '',
          postalCode: updatedClinicProfile.postalCode ?? '',
          openingTime: updatedClinicProfile.openingTime ?? '08:00',
          closingTime: updatedClinicProfile.closingTime ?? '17:00',
        };
        this.notificationService.success('Practice profile saved successfully.');
        this.isSaving = false;
      },
      error: (error) => {
        this.notificationService.error(
          'Failed to save practice profile: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
        this.isSaving = false;
      },
    });
  }
}
